import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PackageOpen, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

import Button from '../../../components/ui/Button';
import HandoverItemsEditor, { parseHandoverItems } from '../../../components/HandoverItemsEditor';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 14px 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  strong { font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold}; }
`;

const Note = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.4);
  border-radius: 12px;
  padding: 14px 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #8A6D00;

  div:first-child {
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
    margin-bottom: 4px;
  }
`;

const Ack = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  background: rgba(2, 211, 191, 0.12);
  color: ${({ theme }) => theme.colors.secondary.dark};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 8px 14px;
  border-radius: 999px;
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

export default function HandoverTab({ home, handover, onSaveDraft, onSubmit, onAcknowledge }) {
  const { t } = useTranslation();
  const status = handover?.status || 'draft';
  const editable = status === 'draft' || status === 'rejected';
  const awaitingAck = status === 'approved' && !handover?.employeeAcknowledgedAt;
  const offboardingId = home?.offboardingId || handover?.offboardingId;

  const initialItems = useMemo(() => parseHandoverItems(handover?.itemsJson), [handover?.itemsJson]);
  const [items, setItems] = useState(initialItems);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setItems(initialItems); }, [initialItems]);

  const runSave = async () => {
    setBusy(true);
    await onSaveDraft(offboardingId, JSON.stringify(items));
    setBusy(false);
  };

  const runSubmit = async () => {
    setBusy(true);
    await onSubmit(offboardingId, JSON.stringify(items));
    setBusy(false);
  };

  const runAck = async () => {
    setBusy(true);
    await onAcknowledge();
    setBusy(false);
  };

  return (
    <Wrap>
      <StatusBar>
        <PackageOpen size={18} color="#0291EF" />
        <span>
          {t('myOffboarding.handover.statusLabel')}:{' '}
          <strong>{t(`myOffboarding.handover.status.${status}`, { defaultValue: status })}</strong>
        </span>
      </StatusBar>

      {handover?.rejectionNoteMarkdown && (
        <Note>
          <div>{t('myOffboarding.handover.rejectionNote')}</div>
          <div>{handover.rejectionNoteMarkdown}</div>
        </Note>
      )}

      {!editable && !awaitingAck && status !== 'approved' && (
        <Note>
          <div><AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> {t('myOffboarding.handover.awaitingApproval')}</div>
        </Note>
      )}

      <HandoverItemsEditor
        items={items}
        onChange={setItems}
        editable={editable}
        emptyText={t('myOffboarding.handover.empty')}
      />

      {awaitingAck && (
        <Footer>
          <Button variant="primary" disabled={busy} onClick={runAck}>
            {busy ? <Loader2 size={16} /> : t('myOffboarding.handover.action.acknowledge')}
          </Button>
        </Footer>
      )}

      {status === 'acknowledged' && (
        <Ack>
          <CheckCircle2 size={16} />
          {t('myOffboarding.handover.acknowledged')}
        </Ack>
      )}

      {editable && (
        <Footer>
          <Button variant="outline" disabled={busy} onClick={runSave}>
            {busy ? <Loader2 size={16} /> : t('myOffboarding.handover.action.saveDraft')}
          </Button>
          <Button variant="primary" disabled={busy || items.length === 0} onClick={runSubmit}>
            {busy ? <Loader2 size={16} /> : t('myOffboarding.handover.action.submit')}
          </Button>
        </Footer>
      )}
    </Wrap>
  );
}
